import { CircularProgress } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  AiOutlineArrowDown,
  AiOutlineArrowUp,
  AiOutlinePlusCircle,
} from 'react-icons/ai';
import { FaArrowsAltH } from 'react-icons/fa';
import { useProduto } from '~/pages/Produto/hooks/useProduto';
import { Icon, InputIconContainer } from '~/pages/Produto/styles';
import api from '~/services/api';

import { AlertBox, IconBetween } from './styles';

export const Plu: React.FC = () => {
  const [tipo, setTipo] = useState(0); // 0 - PLU alto, // 1 - PLU baixo
  const [isLoading, setIsLoading] = useState(false);

  const { isUpdate, setValue, getValues } = useProduto();

  const storageKey = 'USER_PREFERENCE_PLU';

  const handleChangeTipoPLU = async (tipoPlu: number) => {
    setTipo(tipoPlu === 0 ? 1 : 0);
    if (!isUpdate) localStorage.setItem(storageKey, tipoPlu === 0 ? '1' : '0');

    setValue('cod_produto', '');
    setValue('initInicializado', false);
    await gerarPlu();
  };

  async function handleBuscaPLUBaixo() {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const res = await api.get('/produto/plu-baixo');
      const { data, success } = res.data;

      if (success) {
        const { plu } = data;
        return setValue('cod_produto', plu);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  const gerarPlu = useCallback(async () => {
    if (isLoading) return;

    setIsLoading(true);

    try {
      const value = localStorage.getItem(storageKey);
      const type = value ? parseInt(value, 10) : 0;

      const res = await api.get(`/produto/genplu/${type}`);
      const { data, success } = res.data;

      if (success) {
        const { plu } = data;
        return setValue('cod_produto', plu);
      }
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, tipo, setValue]);

  const addAlert = useCallback((): string => {
    const cod_produto = getValues('cod_produto');
    const inistInicializado = getValues('initInicializado');
    if (inistInicializado && !cod_produto) {
      return 'alert-class';
    }
    return '';
  }, []);

  const verificaTipo = async () => {
    try {
      const cod_produto = getValues('cod_produto');
      if (!cod_produto) return;
      const { data } = await api.get('/verifica-tipo/plu', {
        params: {
          cod_plu: cod_produto.slice(1, 7),
        },
      });
      setTipo(data?.data?.tipoPlu);
    } catch (error) {
      setTipo(0);
    }
  };

  useEffect(() => {
    if (isUpdate) {
      verificaTipo();
      return;
    }

    const userPreferencePluStorage = localStorage.getItem(storageKey);

    setTipo(
      userPreferencePluStorage ? parseInt(userPreferencePluStorage, 10) : 0,
    );
  }, [isUpdate, storageKey]);

  useEffect(() => {
    if (isUpdate) return;

    // Novo insert OU duplicação
    if (!isUpdate || getValues('duplicaForm') === true) {
      gerarPlu();
    }
  }, [isUpdate, getValues('duplicaForm')]);

  // IDs estáveis para A11y (substitui Field/label do Atlaskit)
  const inputId = useMemo(() => 'plu-input', []);
  const labelId = useMemo(() => 'plu-label', []);

  return (
    <>
      <AlertBox>
        <label
          id={labelId}
          htmlFor={inputId}
          style={{
            marginBottom: 2.5,
            paddingTop: 5,
            fontWeight: 500,
            fontSize: '0.8125rem',
            color: '#6b778c',
          }}
        >
          Código PLU
        </label>

        <InputIconContainer>
          {tipo === 0 && (
            <Icon
              className="icon-left"
              style={{
                backgroundColor: '#28a745',
                height: 'calc(100% - 6px)',
                left: 3,
                top: 3,
                cursor: isUpdate ? 'not-allowed' : 'pointer',
              }}
              disabled={isUpdate}
              title="PLU Alto"
              onClick={async () => {
                if (isUpdate) return;
                await handleChangeTipoPLU(0);
              }}
              aria-label="Definir PLU Baixo"
            >
              <AiOutlineArrowUp style={{ color: '#fff' }} />
            </Icon>
          )}

          {tipo === 1 && (
            <Icon
              className="icon-left"
              style={{
                backgroundColor: '#ffc107',
                height: 'calc(100% - 6px)',
                left: 3,
                top: 3,
                cursor: isUpdate ? 'not-allowed' : 'pointer',
              }}
              disabled={isUpdate}
              title="PLU Baixo"
              onClick={async () => {
                if (isUpdate) return;
                await handleChangeTipoPLU(1);
              }}
              aria-label="Definir PLU Alto"
            >
              <AiOutlineArrowDown style={{ color: '#000' }} />
            </Icon>
          )}

          <input
            id={inputId}
            aria-labelledby={labelId}
            className="input input-produto"
            name="cod_produto"
            type="text"
            value={getValues('cod_produto') ?? ''}
            disabled
            style={{
              textAlign: 'right',
              padding: '0px 40px',
              backgroundColor: 'var(--ds-background-default,#EBECF0)',
              borderRadius: '3px',
              fontSize: '14px',
              border: '2px solid var(--ds-border-neutral, #DFE1E6)',
              height: 36,
            }}
          />

          <Icon
            className="icon-right"
            style={{
              backgroundColor: '#4C9AFF',
              height: 'calc(100% - 6px)',
              right: 3,
              top: 3,
              cursor: isUpdate ? 'not-allowed' : 'pointer',
            }}
            disabled={isUpdate}
            onClick={() => {
              if (isUpdate) return;
              setValue('initInicializado', false);
              gerarPlu();
            }}
            title="Gerar PLU"
            aria-label="Gerar novo PLU"
          >
            {isLoading ? (
              <CircularProgress size={16} style={{ color: '#fff' }} />
            ) : (
              <AiOutlinePlusCircle style={{ color: '#fff' }} size={24} />
            )}
          </Icon>
        </InputIconContainer>

        <div className={addAlert()} />
      </AlertBox>

      {tipo === 1 && (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id="">Busca o Primeiro PLU Baixo Disponível</Tooltip>
          }
        >
          <IconBetween
            style={{
              cursor: isUpdate ? 'not-allowed' : 'pointer',
              color: '#8850bf',
              backgroundColor: 'transparent',
              border: '1px solid #8850bf',
              marginLeft: 5,
              marginTop: 26,
            }}
            disabled={isUpdate}
            onClick={() => {
              if (isUpdate) return;
              setValue('initInicializado', false);
              handleBuscaPLUBaixo();
            }}
            aria-label="Buscar primeiro PLU baixo disponível"
          >
            {isLoading ? (
              <CircularProgress size={14} />
            ) : (
              <FaArrowsAltH fontSize={14} size={14} />
            )}
          </IconBetween>
        </OverlayTrigger>
      )}
    </>
  );
};
